import { useState } from "react";
import { ThumbsUp, ThumbsDown, Printer, Share2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FAQQuestion } from "@/types/faq";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";

interface FAQAnswerContentProps {
  question: FAQQuestion;
  relatedQuestions?: FAQQuestion[];
  onRelatedClick?: (questionId: string) => void;
}

export const FAQAnswerContent = ({
  question,
  relatedQuestions = [],
  onRelatedClick,
}: FAQAnswerContentProps) => {
  const { toast } = useToast();
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [yesCount, setYesCount] = useState(question.votes);
  const [noCount, setNoCount] = useState(Math.floor(question.votes * 0.1));

  const handleVote = (isHelpful: boolean) => {
    if (helpful === null) {
      setHelpful(isHelpful);
      if (isHelpful) {
        setYesCount((prev) => prev + 1);
      } else {
        setNoCount((prev) => prev + 1);
      }
      toast({ title: "Mulțumim pentru feedback!" });
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({ title: "Link copiat în clipboard!" });
  };

  const handlePrint = () => {
    window.print();
    toast({ title: "Pregătit pentru imprimare" });
  };

  const handleDownload = () => {
    toast({ title: "Funcționalitate în dezvoltare" });
  };

  return (
    <div className="space-y-8">
      {/* Question Header */}
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {question.urgent && <Badge variant="destructive">Urgent</Badge>}
          {question.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-foreground">
          {question.question}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span>
            Ultima actualizare:{" "}
            {new Date(question.lastUpdated).toLocaleDateString("ro-RO", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
          {question.articleReference && (
            <>
              <span>•</span>
              <span>{question.articleReference}</span>
            </>
          )}
        </div>
      </div>

      <Separator />

      {/* Answer Content */}
      <div className="prose prose-lg max-w-none">
        <div className="whitespace-pre-line text-foreground leading-relaxed">
          {question.answer}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 pt-4">
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer className="w-4 h-4 mr-2" />
          Imprimă
        </Button>
        <Button variant="outline" size="sm" onClick={handleShare}>
          <Share2 className="w-4 h-4 mr-2" />
          Distribuie
        </Button>
        <Button variant="outline" size="sm" onClick={handleDownload}>
          <Download className="w-4 h-4 mr-2" />
          Descarcă PDF
        </Button>
      </div>

      <Separator />

      {/* Helpful Section */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4">A fost util acest răspuns?</h3>
          <div className="flex items-center gap-4">
            <Button
              variant={helpful === true ? "default" : "outline"}
              onClick={() => handleVote(true)}
              disabled={helpful !== null}
              className="gap-2"
            >
              <ThumbsUp className="w-4 h-4" />
              Da ({yesCount.toLocaleString("ro-RO")})
            </Button>
            <Button
              variant={helpful === false ? "destructive" : "outline"}
              onClick={() => handleVote(false)}
              disabled={helpful !== null}
              className="gap-2"
            >
              <ThumbsDown className="w-4 h-4" />
              Nu ({noCount.toLocaleString("ro-RO")})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Related Questions */}
      {relatedQuestions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-foreground">
            Întrebări conexe
          </h2>
          <div className="grid gap-4">
            {relatedQuestions.map((related) => (
              <Card
                key={related.id}
                className="cursor-pointer hover:shadow-md transition-all"
                onClick={() => onRelatedClick?.(related.id)}
              >
                <CardContent className="p-4">
                  <h4 className="font-medium text-foreground hover:text-primary transition-colors">
                    {related.question}
                  </h4>
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <ThumbsUp className="w-3 h-3" />
                    <span>{related.votes.toLocaleString("ro-RO")} voturi</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
